import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Lock, Terminal } from "lucide-react";
import { api, btnGhost, fmtErr, input } from "./shared";

const fmtT = (iso) => iso ? new Date(iso + (iso.endsWith("Z") ? "" : "Z")).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

export const EmergencyHoliday = ({ isCeo }) => {
  const [items, setItems] = useState([]);
  const [day, setDay] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/biometric/emergency-holidays").then((r) => setItems(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const activate = async () => {
    if (!window.confirm(`Declare ${day} an EMERGENCY HOLIDAY?\n\nEvery biometric device locks for all staff (CEO exempt) on that day — no fingerprint, face, card or PIN entry. The day is marked as a company holiday (no absent / LOP). Access restores automatically on the next working day.`)) return;
    setBusy(true);
    try { await api.post("/biometric/emergency-holiday", { date: day, note }); toast.success(`Emergency holiday set for ${day}`); setNote(""); load(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const cancel = async (d) => {
    if (!window.confirm(`Cancel the emergency holiday on ${d}? Devices are released immediately if the lock is already active.`)) return;
    try { await api.delete(`/biometric/emergency-holiday/${d}`); toast.success("Emergency holiday cancelled"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const live = items.filter((i) => !i.cancelled_at);
  return (
    <div className="border border-red-500/30 bg-red-500/[0.04] p-4 sm:p-5" data-testid="emergency-holiday-panel">
      <p className="text-[10px] tracking-[0.24em] uppercase text-red-300 flex items-center gap-2 mb-3"><AlertTriangle size={13} /> Emergency Holiday — device lockdown</p>
      {isCeo && (
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input type="date" min={today()} value={day} onChange={(e) => setDay(e.target.value)} className={`${input} sm:w-44`} data-testid="emergency-date" />
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={80} placeholder="Reason (optional, e.g. cyclone warning)" className={`${input} flex-1`} data-testid="emergency-note" />
          <button onClick={activate} disabled={busy || !day} className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-semibold text-xs tracking-[0.12em] uppercase px-4 py-2.5 flex items-center justify-center gap-2" data-testid="emergency-activate">
            <Lock size={13} /> {busy ? "Setting…" : "Activate Emergency Holiday"}
          </button>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mb-2">Locks every ZKTeco / ESSL device for all staff except the CEO (Hikvision: punches rejected + CEO alert). The date is a company holiday — nobody is marked absent or LOP. Auto-restores on the next working day.</p>
      {live.length === 0 ? <p className="text-xs text-slate-500">No emergency holidays scheduled.</p> : (
        <div className="flex flex-col gap-1.5">
          {live.map((i) => (
            <div key={i.date} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-white/10 px-3 py-2" data-testid={`emergency-row-${i.date}`}>
              <span className="text-white font-medium">{i.date}</span>
              {i.note && <span className="text-slate-400">{i.note}</span>}
              <span className={i.released_at ? "text-emerald-300" : i.applied_at ? "text-red-300" : "text-amber-300"}>{i.released_at ? `released ${fmtT(i.released_at)}` : i.applied_at ? `LOCKED since ${fmtT(i.applied_at)} · ${(i.blocked || []).length} staff` : "scheduled"}</span>
              <span className="text-slate-500 ml-auto">by {i.by}</span>
              {isCeo && !i.released_at && <button onClick={() => cancel(i.date)} className={`${btnGhost} !py-1 !px-2 text-[10px]`} data-testid={`emergency-cancel-${i.date}`}>Cancel</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DeviceCommands = ({ isCeo, devices = [] }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sn, setSn] = useState("");
  const load = useCallback(() => api.get("/biometric/device-cmds?limit=60").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { if (open) { load(); const t = setInterval(load, 10000); return () => clearInterval(t); } }, [open, load]);
  const adms = devices.filter((d) => d.vendor !== "hikvision");
  const test = async (step) => {
    const target = sn || adms[0]?.sn;
    if (!target) return toast.error("No ADMS device");
    try { const { data: r } = await api.post("/biometric/device-block/test", { sn: target, step }); toast.success(`Queued #${r.id}: ${step} on ${target} — watch the reply below`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const status = (s) => s === "done" ? "text-emerald-300" : s === "failed" ? "text-red-300" : s === "sent" ? "text-amber-300" : "text-slate-400";
  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="device-commands">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left" data-testid="device-commands-toggle">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><Terminal size={13} className="text-nx-goldlight" /> Device block commands (ADMS) · every device reply is logged</p>
        <span className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 flex flex-col gap-3">
          {isCeo && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500">Test PIN 9999 (password 1234) on</span>
              <select value={sn} onChange={(e) => setSn(e.target.value)} className={`${input} !w-auto !py-1.5`} data-testid="device-test-sn">
                {adms.map((d) => <option key={d.sn} value={d.sn}>{d.name || d.sn}</option>)}
              </select>
              {["create", "block", "open", "query", "delete"].map((s) => <button key={s} onClick={() => test(s)} className={`${btnGhost} !py-1 !px-2.5 text-[10px]`} data-testid={`device-test-${s}`}>{s}</button>)}
            </div>
          )}
          {(data?.blocks || []).length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Active blocks</p>
              {data.blocks.filter((b) => b.state !== "restored").map((b) => (
                <div key={`${b.email}-${b.sn}`} className="text-xs flex flex-wrap gap-x-3 border border-white/10 px-3 py-1.5" data-testid={`device-block-${b.sn}-${b.uid}`}>
                  <span className="text-white">{b.email}</span><span className="text-slate-400">{b.sn} · PIN {b.uid}</span>
                  <span className={b.state === "needs_attention" ? "text-red-300" : b.state === "validity" || b.state === "deleted" ? "text-emerald-300" : "text-amber-300"}>{b.state}</span>
                  <span className="text-slate-500">{b.why}</span>
                </div>
              ))}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] min-w-[560px]">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">#</th><th className="pr-3">Device</th><th className="pr-3">Command</th><th className="pr-3">Status</th><th className="pr-3">Reply</th><th>Sent</th></tr></thead>
              <tbody>
                {(data?.cmds || []).map((c) => (
                  <tr key={c.id} className="border-t border-white/5 align-top" data-testid={`device-cmd-${c.id}`}>
                    <td className="py-1 pr-3 text-slate-400">{c.id}</td><td className="pr-3 text-slate-300">{c.sn}</td>
                    <td className="pr-3 font-mono text-slate-200 break-all max-w-[320px]">{c.cmd.length > 90 ? c.cmd.slice(0, 90) + "…" : c.cmd}</td>
                    <td className={`pr-3 ${status(c.status)}`}>{c.status}</td><td className="pr-3 font-mono text-slate-400 break-all">{c.result || "—"}</td><td className="text-slate-500 whitespace-nowrap">{fmtT(c.sent_at || c.created_at)}</td>
                  </tr>
                ))}
                {(data?.cmds || []).length === 0 && <tr><td colSpan={6} className="py-2 text-slate-500">No commands yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
